import React, { useState } from "react";
import { generateDocx } from "@/lib/api";
import { Download, Printer, Loader2, CheckCircle2 } from "lucide-react";
import { TEMPLATES } from "./ResumeTemplates";

export default function ResumePreview({ structured, targetRole, initialTemplate = "modern" }) {
  const [template, setTemplate] = useState(initialTemplate);
  const [downloading, setDownloading] = useState(false);
  const [downloaded, setDownloaded] = useState(false);

  if (!structured) return null;

  const active = TEMPLATES.find(t => t.id === template);
  const Component = active?.Component;

  const handleDocx = async () => {
    setDownloading(true);
    try {
      const { data } = await generateDocx(structured, template);
      const blob = new Blob([data], {
        type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const name = structured?.personal?.name || "resume";
      a.download = `${name.replace(/\s+/g, "-").toLowerCase()}-${template}.docx`;
      a.click();
      URL.revokeObjectURL(url);
      setDownloaded(true);
      setTimeout(() => setDownloaded(false), 2500);
    } catch (e) {
      console.error("DOCX download failed", e);
    } finally {
      setDownloading(false);
    }
  };

  const handlePrint = () => {
    // Open resume in a print window
    const html = document.getElementById("resume-preview-content")?.innerHTML;
    if (!html) return;
    const win = window.open("", "_blank", "width=820,height=1100");
    win.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8"/>
        <title>Resume</title>
        <link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/tailwindcss@2.2.19/dist/tailwind.min.css"/>
        <style>
          @page { margin: 0; size: A4; }
          body { margin: 0; padding: 0; }
          @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
        </style>
      </head>
      <body>${html}</body>
      </html>
    `);
    win.document.close();
    win.focus();
    setTimeout(() => { win.print(); }, 600);
  };

  return (
    <div>
      {/* Template selector */}
      <div className="flex flex-wrap gap-3 mb-5">
        {TEMPLATES.map(t => (
          <button
            key={t.id}
            onClick={() => setTemplate(t.id)}
            className={`flex flex-col items-start px-4 py-3 rounded-2xl border text-left transition-all ${
              template === t.id
                ? "border-stone-900 bg-stone-900 text-stone-50"
                : "border-stone-200 hover:border-stone-400 bg-white"
            }`}
          >
            <span className="font-display text-sm font-medium">{t.label}</span>
            <span className={`text-xs mt-0.5 ${template === t.id ? "text-stone-400" : "text-stone-400"}`}>{t.desc}</span>
          </button>
        ))}
      </div>

      {/* Action buttons */}
      <div className="flex gap-3 mb-5">
        <button
          onClick={handleDocx}
          disabled={downloading}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-stone-900 text-stone-50 hover:bg-[#C2410C] transition-colors disabled:opacity-60 text-sm font-medium"
        >
          {downloading ? <Loader2 size={14} className="animate-spin" /> : downloaded ? <CheckCircle2 size={14} /> : <Download size={14} />}
          {downloading ? "Generating .docx…" : downloaded ? "Downloaded!" : "Download .docx"}
        </button>
        <button
          onClick={handlePrint}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-stone-200 text-stone-700 hover:bg-stone-50 transition-colors text-sm font-medium"
        >
          <Printer size={14} /> Print / Save PDF
        </button>
      </div>

      {/* ATS badge */}
      <div className="flex items-center gap-2 mb-4">
        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-medium">
          <CheckCircle2 size={12} /> ATS-optimised
        </span>
        {structured?.keywords_included?.length > 0 && (
          <span className="text-xs text-stone-400">{structured.keywords_included.length} keywords included</span>
        )}
        {structured?.word_count && (
          <span className="text-xs text-stone-400">{structured.word_count} words</span>
        )}
      </div>

      {/* Preview — scaled to fit */}
      <div className="rounded-2xl border-2 border-stone-200 overflow-hidden shadow-lg">
        <div
          id="resume-preview-content"
          style={{ transform: "scale(0.72)", transformOrigin: "top left", width: "138.89%", height: "auto" }}
        >
          {Component && <Component data={structured} />}
        </div>
      </div>

      {/* ATS tips */}
      {structured?.ats_tips?.length > 0 && (
        <div className="mt-4 p-4 rounded-2xl bg-amber-50 border border-amber-100">
          <p className="text-xs font-medium text-amber-700 mb-2 uppercase tracking-widest font-mono">ATS Tips</p>
          <ul className="space-y-1">
            {structured.ats_tips.map((tip, i) => (
              <li key={i} className="text-xs text-amber-800 flex gap-1.5"><span>•</span>{tip}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
